import { UseControllerProps } from 'react-hook-form';

import { Country } from '@xemplo/country-helper';
import { InputSize } from '@xemplo/input-utils';
import { XemploInputProps } from '@xemplo/xemplo-input';

export const NumberFormat = {
  Decimal: 'decimal',
  Currency: 'currency',
  CurrencyValue: 'currencyValue',
  Percent: 'percent',
} as const;
export type NumberFormat = (typeof NumberFormat)[keyof typeof NumberFormat];

export const StepperDirection = {
  Up: 'up',
  Down: 'down',
} as const;
export type StepperDirection = (typeof StepperDirection)[keyof typeof StepperDirection];

export const EventType = {
  Change: 'change',
  Blur: 'blur',
  Wheel: 'wheel',
  KeyDown: 'keydown',
  Stepper: 'stepper',
} as const;
export type EventType = (typeof EventType)[keyof typeof EventType];

/**
 * NumberFieldSize defines the available sizes for the NumberField component.
 * The UX team is changing the naming convention for sizes.
 * - Spacious: Large
 * - Default: Standard
 * - Compact: Medium
 */
export const NumberFieldSize = {
  Spacious: InputSize.Spacious,
  Default: InputSize.Default,
  Compact: InputSize.Compact,
} as const;
export type NumberFieldSize = (typeof NumberFieldSize)[keyof typeof NumberFieldSize];

export type NumberFormatOptions = Pick<
  Intl.NumberFormatOptions,
  'minimumFractionDigits' | 'maximumFractionDigits'
> & {
  /** The locale to use for formatting the number.
   * If not provided, it will default to the browser's locale.
   * This is useful for ensuring consistent formatting across different regions.
   * @default navigator.language
   */
  locale?: Country.Locale;

  /**
   * The format of the number input
   * - $1,234.56 for Currency
   * - 1,234.56 for Decimal
   * - AUD 1,234.56 for CurrencyValue
   * - 1,234% for Percent
   *
   * NOTE: Number format (use of commas, periods, etc.) is determined by the locale.
   * CurrencyValue is also affected by the locale.
   *
   * @default NumberFormat.Decimal
   */
  numberFormat?: NumberFormat;

  /**
   * In some cases we would want to display a unit for the number input.
   * e.g. "kg", "m", "cm", etc.
   * This would work only with the Decimal number format.
   */
  unit?: string;
};

export type NumberFieldProps = Omit<
  XemploInputProps,
  'type' | 'inputSize' | 'trailingIcon'
> & {
  /** Provides the number formatting options */
  formatOptions?: NumberFormatOptions;

  /** The input size for the NumberField component. */
  inputSize?: NumberFieldSize;

  /**
   * This event is mostly used to track changes in the input value
   * and provides the raw input value use with caution, as it may
   * not always reflect what you are looking for.
   * It was mostly added to support the React Hook Form integration.
   */
  onValueChange?: (value: number | string | undefined, eventType: EventType) => void;
};

export type NumberFieldRhfProps = Omit<
  NumberFieldProps,
  'value' | 'onValueChange' | 'min' | 'max'
> & {
  rules?: UseControllerProps['rules'];
};
